import { useState } from "react";
import { Link } from "react-router-dom";
import type { Quote } from "@fprint/shared";
import { Button, buttonClasses } from "@fprint/ui";

import { submitQuote, type TokenGetter } from "../lib/api";
import { markResumeAfterAuth, persistResumeQuoteId } from "../lib/resumeQuote";

/**
 * Confirmation shown once a quote has been submitted for operator review.
 * Exported separately so the order page's 409 review-required recovery path
 * can render the exact same confirmation.
 */
export function ReviewSubmittedNotice({ qty }: { qty: number }) {
  return (
    <div
      role="status"
      data-testid="review-submitted"
      className="rounded-md border border-success bg-success/10 px-4 py-3 text-sm text-success-fg"
    >
      <p className="font-medium">Submitted — we&apos;ll email you a final quote</p>
      <p className="mt-1">
        Quantity locked at {qty} {qty === 1 ? "part" : "parts"}. Our operator
        will review the job and you&apos;ll receive the final quote by email.
        No payment is taken now.
      </p>
    </div>
  );
}

interface SubmitForReviewProps {
  quote: Quote;
  getToken: TokenGetter;
  /**
   * MUST already reflect a LOADED auth state (isLoaded && isSignedIn — see
   * the Clerk auth-loading discipline in CLAUDE.md). When false the CTA
   * routes to sign-in carrying /order/:id as the resume target, exactly like
   * RequireAuth/SignInPage do, so the user returns to this quote after login.
   */
  signedIn: boolean;
}

/**
 * Qty picker + "Submit for review" CTA for an `estimated` quote (a machine
 * estimate that is not payable until an operator reviews it). On success it
 * swaps itself for the submitted confirmation with the locked quantity.
 */
export function SubmitForReview({ quote, getToken, signedIn }: SubmitForReviewProps) {
  const [qty, setQty] = useState(quote.submitted_qty ?? 1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<Quote | null>(null);

  if (submitted) {
    return <ReviewSubmittedNotice qty={submitted.submitted_qty ?? qty} />;
  }

  if (!signedIn) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-sm text-muted-fg">
          Sign in to submit this estimate for review — you&apos;ll come right
          back to this quote.
        </p>
        <Link
          to={`/sign-in?from=${encodeURIComponent(`/order/${quote.id}`)}`}
          state={{ from: `/order/${quote.id}` }}
          className={buttonClasses({ size: "lg", className: "self-start" })}
          data-testid="submit-review-signin"
          onClick={() => {
            persistResumeQuoteId(quote.id);
            markResumeAfterAuth();
          }}
        >
          Sign in to submit for review
        </Link>
      </div>
    );
  }

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const updated = await submitQuote(quote.id, qty, getToken);
      setSubmitted(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <label className="flex max-w-xs flex-col gap-1 text-sm">
        <span className="font-medium">Quantity</span>
        <input
          type="number"
          min={1}
          max={50}
          value={qty}
          disabled={submitting}
          onChange={(e) =>
            setQty(Math.max(1, Math.min(50, Number(e.target.value) || 1)))
          }
          className="h-10 rounded-md border border-border bg-bg px-2 disabled:opacity-50"
          data-testid="submit-review-qty"
        />
      </label>

      {error && (
        <p
          role="alert"
          data-testid="submit-review-error"
          className="rounded-md border border-danger bg-danger/10 px-3 py-2 text-sm text-danger-fg"
        >
          {error}
        </p>
      )}

      <Button
        size="lg"
        className="self-start"
        onClick={handleSubmit}
        disabled={submitting}
        data-testid="submit-review"
      >
        {submitting ? "Submitting…" : "Submit for review"}
      </Button>
    </div>
  );
}
